/**
 * UseCasesSection - Use cases by medical specialty + operation scale
 * Shows how NARA adapts to different contexts
 */
import React from 'react';
import { Typography, Box, Container } from '@mui/material';
import LocalHospitalIcon from '@mui/icons-material/LocalHospital';
import FaceRetouchingNaturalIcon from '@mui/icons-material/FaceRetouchingNatural';
import ChildCareIcon from '@mui/icons-material/ChildCare';
import MoodIcon from '@mui/icons-material/Mood';
import PersonIcon from '@mui/icons-material/Person';
import GroupsIcon from '@mui/icons-material/Groups';
import ApartmentIcon from '@mui/icons-material/Apartment';
import MedicalServicesIcon from '@mui/icons-material/MedicalServices';
import BusinessIcon from '@mui/icons-material/Business';

import { COLORS, STYLES, TYPOGRAPHY, SPACING, TRANSITIONS } from './landingTheme';
import { ScrollReveal, StaggerReveal } from './animations';

// Use cases data
const SPECIALTIES = [
  {
    icon: LocalHospitalIcon,
    title: 'Medicina General',
    description: 'Triage de síntomas cotidianos, seguimiento de pacientes crónicos y agendamiento automático de consultas.',
    color: '#0ea5e9',
    tag: 'Especialidad',
    stat: '60%',
    statLabel: 'menos llamadas',
  },
  {
    icon: FaceRetouchingNaturalIcon,
    title: 'Dermatología',
    description: 'Análisis de imágenes de lesiones cutáneas con IA, seguimiento fotográfico y derivación según urgencia.',
    color: '#ec4899',
    tag: 'Especialidad',
    stat: 'IA',
    statLabel: 'análisis visual',
  },
  {
    icon: ChildCareIcon,
    title: 'Pediatría',
    description: 'Atención a padres preocupados con tono empático, orientación sobre síntomas infantiles y recordatorios de vacunas.',
    color: '#8b5cf6',
    tag: 'Especialidad',
    stat: '24/7',
    statLabel: 'atención padres',
  },
  {
    icon: MoodIcon,
    title: 'Odontología',
    description: 'Gestión de citas para procedimientos, recordatorios de revisiones periódicas y seguimiento post-tratamiento.',
    color: '#10b981',
    tag: 'Especialidad',
    stat: '0%',
    statLabel: 'citas perdidas',
  },
];

const SCALES = [
  {
    icon: PersonIcon,
    title: 'Consultorio Individual',
    description: 'Un doctor, un chatbot. Automatiza tu atención fuera de horario y deja que NARA maneje citas y consultas iniciales.',
    color: '#f59e0b',
    highlight: '1 chatbot',
  },
  {
    icon: GroupsIcon,
    title: 'Clínica Multi-doctor',
    description: 'Cada doctor con su propio asistente personalizado, planes individuales y base de conocimiento independiente.',
    color: '#6366f1',
    highlight: '3-10 chatbots',
  },
  {
    icon: ApartmentIcon,
    title: 'Centro Médico',
    description: 'Múltiples especialidades bajo un mismo panel de control. Métricas unificadas, gestión centralizada de suscripciones.',
    color: '#0ea5e9',
    highlight: '10+ chatbots',
  },
];

// Specialty card
const SpecialtyCard = ({ item }) => {
  const IconComponent = item.icon;

  return (
    <Box
      sx={{
        p: { xs: 2.5, md: 3 },
        borderRadius: 4,
        background: COLORS.white,
        border: `1px solid ${COLORS.gray200}`,
        height: '100%',
        cursor: 'pointer',
        transition: TRANSITIONS.normal,
        position: 'relative',
        overflow: 'hidden',
        boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
        '&::after': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: 3,
          background: `linear-gradient(90deg, ${item.color}, ${item.color}50)`,
          opacity: 0,
          transition: TRANSITIONS.normal,
        },
        '&:hover': {
          transform: { xs: 'none', sm: 'translateY(-4px)' },
          boxShadow: `0 12px 32px ${item.color}12, 0 4px 12px rgba(0,0,0,0.05)`,
          borderColor: `${item.color}30`,
          '&::after': { opacity: 1 },
          '& .spec-icon': {
            transform: 'scale(1.1)',
            boxShadow: `0 8px 20px ${item.color}20`,
          },
        },
      }}
    >
      {/* Tag */}
      <Typography
        sx={{
          fontSize: '0.65rem',
          fontWeight: 700,
          color: item.color,
          textTransform: 'uppercase',
          letterSpacing: '0.08em',
          mb: 1.5,
        }}
      >
        {item.tag}
      </Typography>

      {/* Icon */}
      <Box
        className="spec-icon"
        sx={{
          width: 44,
          height: 44,
          borderRadius: 2.5,
          background: `linear-gradient(135deg, ${item.color}12 0%, ${item.color}06 100%)`,
          border: `1px solid ${item.color}15`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          mb: 2,
          transition: TRANSITIONS.normal,
        }}
      >
        <IconComponent sx={{ fontSize: 22, color: item.color }} />
      </Box>

      {/* Content */}
      <Typography
        sx={{
          fontSize: { xs: '0.95rem', md: '1.05rem' },
          fontWeight: 700,
          color: COLORS.textPrimary,
          lineHeight: 1.3,
          mb: 1,
        }}
      >
        {item.title}
      </Typography>
      <Typography
        sx={{
          fontSize: '0.84rem',
          color: COLORS.textSecondary,
          lineHeight: 1.65,
          mb: 1.5,
        }}
      >
        {item.description}
      </Typography>

      {/* Stat pill */}
      {item.stat && (
        <Box
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 0.6,
            px: 1,
            py: 0.35,
            borderRadius: 1.5,
            background: `${item.color}08`,
            border: `1px solid ${item.color}15`,
            mt: 'auto',
          }}
        >
          <Typography sx={{ fontSize: '0.75rem', fontWeight: 800, color: item.color }}>
            {item.stat}
          </Typography>
          <Typography sx={{ fontSize: '0.68rem', fontWeight: 500, color: COLORS.textMuted }}>
            {item.statLabel}
          </Typography>
        </Box>
      )}
    </Box>
  );
};

// Scale card — horizontal layout with highlight badge
const ScaleCard = ({ item }) => {
  const IconComponent = item.icon;

  return (
    <Box
      sx={{
        p: { xs: 2.5, md: 3 },
        borderRadius: 4,
        background: COLORS.white,
        border: `1px solid ${COLORS.gray200}`,
        height: '100%',
        cursor: 'pointer',
        transition: TRANSITIONS.normal,
        position: 'relative',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'flex-start',
        gap: 2,
        boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
        // Left accent
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          width: 3,
          bottom: 0,
          background: item.color,
          borderRadius: '12px 0 0 12px',
          opacity: 0,
          transition: TRANSITIONS.normal,
        },
        '&:hover': {
          transform: { xs: 'none', sm: 'translateY(-4px)' },
          boxShadow: `0 12px 32px ${item.color}12, 0 4px 12px rgba(0,0,0,0.05)`,
          borderColor: `${item.color}30`,
          '&::before': { opacity: 1 },
          '& .scale-icon': {
            transform: 'scale(1.1)',
            boxShadow: `0 8px 20px ${item.color}20`,
          },
        },
      }}
    >
      {/* Icon */}
      <Box
        className="scale-icon"
        sx={{
          width: 48,
          height: 48,
          minWidth: 48,
          borderRadius: 3,
          background: `linear-gradient(135deg, ${item.color}14 0%, ${item.color}08 100%)`,
          border: `1px solid ${item.color}15`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          transition: TRANSITIONS.normal,
        }}
      >
        <IconComponent sx={{ fontSize: 24, color: item.color }} />
      </Box>

      {/* Content */}
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.75 }}>
          <Typography
            sx={{
              fontSize: { xs: '0.95rem', md: '1.05rem' },
              fontWeight: 700,
              color: COLORS.textPrimary,
              lineHeight: 1.3,
            }}
          >
            {item.title}
          </Typography>
          <Box
            sx={{
              px: 1,
              py: 0.2,
              borderRadius: 1,
              background: `${item.color}10`,
              border: `1px solid ${item.color}20`,
              flexShrink: 0,
            }}
          >
            <Typography sx={{ fontSize: '0.68rem', fontWeight: 700, color: item.color }}>
              {item.highlight}
            </Typography>
          </Box>
        </Box>
        <Typography
          sx={{
            fontSize: '0.84rem',
            color: COLORS.textSecondary,
            lineHeight: 1.65,
          }}
        >
          {item.description}
        </Typography>
      </Box>
    </Box>
  );
};

export default function UseCasesSection() {
  return (
    <Box sx={{ py: SPACING.sectionY, background: `linear-gradient(180deg, ${COLORS.white} 0%, ${COLORS.gray50} 100%)`, position: 'relative', overflow: 'hidden' }}>
      {/* Decorative blob */}
      <Box sx={{ position: 'absolute', bottom: '-10%', right: '-5%', width: 400, height: 400, borderRadius: '50%', background: 'radial-gradient(circle, rgba(14, 165, 233, 0.04) 0%, transparent 70%)', pointerEvents: 'none' }} />

      <Container maxWidth="lg">
        {/* Section header */}
        <ScrollReveal>
        <Box sx={{ textAlign: 'center', mb: { xs: 5, md: 7 } }}>
          <Typography
            sx={{
              color: COLORS.accent,
              fontSize: TYPOGRAPHY.tiny,
              fontWeight: 700,
              textTransform: 'uppercase',
              letterSpacing: '0.1em',
              mb: 1.5,
            }}
          >
            Casos de uso
          </Typography>
          <Typography
            variant="h2"
            sx={{ ...STYLES.sectionTitle }}
          >
            Se adapta a tu especialidad y a tu escala
          </Typography>
          <Typography
            sx={{ ...STYLES.sectionSubtitle }}
          >
            NARA funciona para cualquier profesional de salud, desde un consultorio individual hasta un centro médico completo
          </Typography>
        </Box>
        </ScrollReveal>

        {/* Specialties — subtitle */}
        <ScrollReveal>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2.5 }}>
          <MedicalServicesIcon sx={{ fontSize: 18, color: COLORS.accent }} />
          <Typography
            sx={{
              fontSize: { xs: '0.8rem', md: '0.85rem' },
              fontWeight: 700,
              color: COLORS.textMuted,
              textTransform: 'uppercase',
              letterSpacing: '0.06em',
            }}
          >
            Por especialidad
          </Typography>
        </Box>
        </ScrollReveal>

        {/* Specialties grid — 4 columns */}
        <StaggerReveal
          staggerDelay={0.1}
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)' },
            gap: { xs: 2.5, sm: 3, md: 4 },
            mb: { xs: 5, md: 7 },
          }}
        >
          {SPECIALTIES.map((item, idx) => (
            <SpecialtyCard key={idx} item={item} />
          ))}
        </StaggerReveal>

        {/* Scale sub-section — subtle background */}
        <Box
          sx={{
            mx: { xs: -2, sm: -3, md: -4 },
            px: { xs: 2, sm: 3, md: 4 },
            py: { xs: 3, md: 4 },
            borderRadius: { xs: 0, md: 4 },
            background: `linear-gradient(135deg, ${COLORS.gray50} 0%, rgba(245, 158, 11, 0.03) 100%)`,
            border: { md: `1px solid ${COLORS.gray200}` },
          }}
        >
          {/* Scale — subtitle */}
          <ScrollReveal>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2.5 }}>
            <BusinessIcon sx={{ fontSize: 18, color: COLORS.accent }} />
            <Typography
              sx={{
                fontSize: { xs: '0.8rem', md: '0.85rem' },
                fontWeight: 700,
                color: COLORS.textMuted,
                textTransform: 'uppercase',
                letterSpacing: '0.06em',
              }}
            >
              Por tamaño de operación
            </Typography>
          </Box>
          </ScrollReveal>

          {/* Scale grid — 3 columns */}
          <StaggerReveal
            staggerDelay={0.12}
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' },
              gap: { xs: 2.5, sm: 3, md: 4 },
            }}
          >
            {SCALES.map((item, idx) => (
              <ScaleCard key={idx} item={item} />
            ))}
          </StaggerReveal>
        </Box>
      </Container>
    </Box>
  );
}
